import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import InfoComponent from '../MapInfoWindow/';
import { markerProps } from '../MapMarker/propTypes';
import { InfoWindow, GoogleMap, Polyline } from '@react-google-maps/api';
import './styles.scss';
import { Marker } from '@react-google-maps/api';
import { MarkerClusterer } from '@react-google-maps/api';
import { THEME } from '../../common/constants';

const options = {
    imagePath:
        'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
};

/**
 * Google Map Integration
 * @see https://react-google-maps-api-docs.netlify.com/#infobox
 */
function Map({
    id,
    zoom,
    center,
    dataPoints,
    isLoading,
    drawPolyline,
    ...restProps
}) {
    const [showWindow, setShowWindow] = useState(false);
    const [windowContent, setWindowContent] = useState(
        <h1>Info Window Content</h1>,
    );
    const [windowPosition, setWindowPosition] = useState(center);

    const renderMapContent = () => {
        return drawPolyline ? renderPolyline() : renderMarkers();
    };

    const renderPolyline = () => {
        return (
            <Polyline
                path={dataPoints}
                options={{ strokeColor: THEME.PRIMARY_BLUE }}
            />
        );
    };

    const renderMarkers = () => {
        return (
            <MarkerClusterer options={options}>
                {(clusterer) =>
                    dataPoints.map(
                        ({ id, icon, tags, thumbnail, position }) => (
                            <Marker
                                key={`k_${id}`}
                                position={position}
                                className="mapMarker"
                                icon={icon}
                                clusterer={clusterer}
                                onClick={() => {
                                    setWindowPosition(position);
                                    setWindowContent(
                                        <InfoComponent
                                            thumbnail={thumbnail}
                                            tags={tags}
                                        />,
                                    );
                                    setShowWindow(true);
                                }}
                            />
                        ),
                    )
                }
            </MarkerClusterer>
        );
    };

    const renderInfoWindow = () => {
        return showWindow ? (
            <InfoWindow
                position={windowPosition}
                onCloseClick={() => setShowWindow(false)}
            >
                {windowContent}
            </InfoWindow>
        ) : null;
    };

    return (
        <>
            {' '}
            {!isLoading ? (
                <GoogleMap id={id} zoom={zoom} center={center} {...restProps}>
                    {renderMapContent()}
                    {renderInfoWindow()}
                </GoogleMap>
            ) : null}
        </>
    );
}

Map.propTypes = {
    // id for the map element, in order to target it directly
    id: PropTypes.string,

    // determines the zoom level of map
    zoom: PropTypes.number,

    // data points to be shown on the map, contains data for markers and infoWindows
    dataPoints: PropTypes.oneOfType([
        // if data points are markers then the propTypes will be an array of shape with marker properties
        PropTypes.arrayOf(
            PropTypes.shape({
                // id that uniquely idenitifies a marker, to be used a key as well
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

                // Marker props
                ...markerProps,

                // content for the infoWindow
                windowContent: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.node,
                ]),
            }),
        ),
        // if data points are to be drawn as a polyline then the PropTypes will be an array of lat long objects
        PropTypes.arrayOf(
            PropTypes.shape({
                lat: PropTypes.string,
                long: PropTypes.string,
            }),
        ),
    ]),
    // initial centering position of the map
    center: PropTypes.shape({
        // latitude used to center the map
        lat: PropTypes.number,

        // longitude used to center the map
        lng: PropTypes.number,
    }),

    // indicates whether the data is being loaded
    isLoading: PropTypes.bool,
    // indicates whether the data it to be drawn as a polyline
    drawPolyline: PropTypes.bool,
};

Map.defaultProps = {
    id: 'litter-map',
    zoom: 2.4,
    // by default, use 0,0 as centre point to show global map
    center: {
        lat: 0,
        lng: 0,
    },
    isLoading: false,
    drawPolyline: false,
};

export default memo(Map);
